import { useTranslation } from "react-i18next";
import { C } from "@/components/modal/home";
import { formatUSDT } from "@/lib/mockData";
import type { MyAgentsFilter } from "./myAgentsPageUtils";

export type MyAgentsPageHeaderProps = {
  filter: MyAgentsFilter;
  onFilterChange: (filter: MyAgentsFilter) => void;
  total: number;
  activatedCount: number;
  pendingCount: number;
  totalInvestment: number;
};

export function MyAgentsPageHeader({
  filter,
  onFilterChange,
  total,
  activatedCount,
  pendingCount,
  totalInvestment,
}: MyAgentsPageHeaderProps) {
  const { t } = useTranslation();

  const statItems = [
    {
      label: t("myAgentsPage.running"),
      value: activatedCount,
      suffix: t("myAgentsPage.unit"),
      color: C.primary,
    },
    {
      label: t("myAgentsPage.pendingActivation"),
      value: pendingCount,
      suffix: t("myAgentsPage.unit"),
      color: C.amber,
    },
    {
      label: t("myAgentsPage.totalInvestment"),
      value: `$${formatUSDT(totalInvestment)}`,
      suffix: "",
      color: C.cyan,
    },
  ];

  const filterTabs: { key: MyAgentsFilter; label: string }[] = [
    {
      key: "all",
      label: t("myAgentsPage.filterAll", { count: total }),
    },
    {
      key: "activated",
      label: t("myAgentsPage.filterRunning", { count: activatedCount }),
    },
    {
      key: "pending",
      label: t("myAgentsPage.filterPending", { count: pendingCount }),
    },
  ];

  return (
    <div className="space-y-3 mb-4">
      <div className="grid grid-cols-3 gap-2">
        {statItems.map(s => (
          <div
            key={s.label}
            className="text-center py-3 rounded-xl"
            style={{
              background: `${s.color}06`,
              border: `1px solid ${s.color}08`,
            }}
          >
            <div className="text-[9px]" style={{ color: C.dim }}>
              {s.label}
            </div>
            <div
              className="text-lg font-bold mono-num"
              style={{ color: s.color }}
            >
              {s.value}
              {s.suffix ? (
                <span className="text-[9px] font-normal ml-0.5">
                  {s.suffix}
                </span>
              ) : null}
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        {filterTabs.map(tab => (
          <button
            key={tab.key}
            type="button"
            className="text-[11px] px-3 py-1.5 rounded-full font-medium transition-all"
            style={
              filter === tab.key
                ? {
                    background: `${C.primary}15`,
                    color: C.primary,
                    border: `1px solid ${C.primary}20`,
                  }
                : {
                    background: "rgba(255,255,255,0.03)",
                    color: C.dim,
                    border: `1px solid ${C.border}`,
                  }
            }
            onClick={() => onFilterChange(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>
    </div>
  );
}
